import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ROLES } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2 } from "lucide-react";

function RoleSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-full sm:w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(ROLES).map((r) => (
          <SelectItem key={r.key} value={r.key} data-testid={`${testid}-option-${r.key}`}>{r.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MemberRow({ member, index, onRoleChange, onRemove }) {
  const initials = (member.name || member.email)[0].toUpperCase();
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-border/60 bg-background-subtle px-4 py-3" data-testid={`member-row-${index}`}>
      <Avatar className="h-10 w-10 border border-border shrink-0">
        <AvatarImage src={member.picture} alt={member.name} />
        <AvatarFallback className="bg-accent text-olive font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate" data-testid={`member-name-${index}`}>{member.name || member.email}</div>
        <div className="text-xs text-muted-foreground truncate font-mono">{member.email}</div>
      </div>
      <Badge variant={member.registered ? "secondary" : "outline"} className="font-normal shrink-0 w-fit" data-testid={`member-status-${index}`}>
        {member.registered ? "Con acceso" : "Pendiente de entrar"}
      </Badge>
      <RoleSelect value={member.role} onChange={(r) => onRoleChange(member, r)} testid={`member-role-select-${index}`} />
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-destructive shrink-0" data-testid={`remove-member-button-${index}`} aria-label="Quitar usuario">
            <Trash2 className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar a {member.name || member.email} del proyecto?</AlertDialogTitle>
            <AlertDialogDescription>Perderá el acceso a este proyecto. Podrás volver a añadirlo cuando quieras.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-full" onClick={() => onRemove(member)} data-testid="confirm-remove-member-button">Quitar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function MembersManager({ projectId }) {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "cliente" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectId}/members`).then((r) => setMembers(r.data)).catch(() => toast.error("No se pudieron cargar los usuarios."));
  }, [projectId]);

  const add = async () => {
    if (!form.email.trim()) return toast.error("Indica el email del usuario.");
    setSaving(true);
    try {
      const r = await api.post(`/projects/${projectId}/members`, form);
      setMembers((m) => [...m, r.data]);
      setForm({ email: "", name: "", role: "cliente" });
      if (r.data.email_sent) {
        toast.success(`${ROLES[r.data.role].label} añadido. Le hemos enviado un email de invitación.`);
      } else {
        toast.warning(`${ROLES[r.data.role].label} añadido, pero no se pudo enviar el email de invitación.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo añadir el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (member, role) => {
    try {
      const r = await api.patch(`/projects/${projectId}/members/${member.member_id}`, { role });
      setMembers((ms) => ms.map((m) => (m.member_id === member.member_id ? r.data : m)));
      toast.success("Rol actualizado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el rol.");
    }
  };

  const remove = async (member) => {
    try {
      await api.delete(`/projects/${projectId}/members/${member.member_id}`);
      setMembers((ms) => ms.filter((m) => m.member_id !== member.member_id));
      toast.success("Usuario eliminado del proyecto.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo quitar el usuario.");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="members-manager">
      <div className="lg:col-span-4 rounded-2xl border border-border bg-card p-6 h-fit">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
          <UserPlus className="h-5 w-5" />
        </div>
        <h3 className="text-lg font-semibold tracking-tight">Añadir usuario</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-5">Recibirá un email con una invitación para crear su contraseña o entrar con Google.</p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="m-email">Email *</Label>
            <Input id="m-email" type="email" data-testid="member-email-input" placeholder="nombre@correo.com" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-name">Nombre</Label>
            <Input id="m-name" data-testid="member-name-input" placeholder="Ej. Lucía Gómez" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Rol en el proyecto</Label>
            <RoleSelect value={form.role} onChange={(role) => setForm((f) => ({ ...f, role }))} testid="member-role-select" />
            <p className="text-xs text-muted-foreground">{ROLES[form.role].description}</p>
          </div>
          <Button className="w-full rounded-full h-11 mt-2" onClick={add} disabled={saving} data-testid="add-member-button">
            {saving ? "Añadiendo…" : "Añadir al proyecto"}
          </Button>
        </div>
      </div>

      <div className="lg:col-span-8 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold tracking-tight">Usuarios del proyecto</h3>
          <span className="font-mono text-sm text-muted-foreground" data-testid="members-count">{members.length}</span>
        </div>
        <div className="space-y-2">
          {members.map((m, i) => (
            <MemberRow key={m.member_id} member={m} index={i} onRoleChange={changeRole} onRemove={remove} />
          ))}
        </div>
      </div>
    </div>
  );
}
